import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment/moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../../../../../shared/services/auth.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { LocationPickerComponent } from '../../../../../../shared/components/location-picker/location-picker.component';
import { ProjectsService } from '../../../../core/projects.service';
import { UpdateProjectRequest } from '../../../../core/projects.actions';
import { appDataDetails } from '../../../../../../shared/core/shared.selectors';

@Component({
    selector: 'app-edit-project-details',
    templateUrl: './edit-project-details.component.html',
    styleUrls: ['./edit-project-details.component.scss'],
    standalone: false
})
export class EditProjectDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  statusOptions = [
    { value: 'active', label: 'Open' },
    { value: 'inactive', label: 'Close' },
  ];

  addMenuOptions = [
    {
      key: 'location',
      icon: 'marker-outline',
      label: 'Location',
      handler: () => {
        this.openLocationPicker();
      },
    },
  ];

  form = new FormGroup<any>({
    name: new FormControl({ value: null, disabled: false }),
    number: new FormControl({ value: null, disabled: false }),
    client: new FormControl({ value: null, disabled: false }),
    project_start_date: new FormControl({ value: null, disabled: false }),
    project_end_date: new FormControl({ value: null, disabled: false }),
    project_value: new FormControl({ value: null, disabled: false }),
    change_order_days: new FormControl({ value: null, disabled: false }),
    description: new FormControl({ value: null, disabled: false }),
    status: new FormControl({ value: null, disabled: false }),
    latitude: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: '[]', disabled: false }),
  });
  mediaFiles: any[] = [];

  errorMessages: any = {};
  isFormDirty = false;
  private readonly onDestroy = new Subject<void>();

  errorMessageMap: Record<string, Record<string, string>> = {
    name: {
      required: 'This field is required.',
    },
    number: {
      required: 'This field is required.',
    },
    project_start_date: {
      required: 'This field is required.',
    },
    project_end_date: {
      required: 'This field is required.',
    },
    project_value: {
      required: 'This field is required.',
    },
    change_order_days: {
      required: 'This field is required.',
    },
    description: {
      required: 'This field is required.',
    },
  };

  constructor(
    private authService: AuthService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private projectsService: ProjectsService,
    private store: Store,
  ) {}

  isOrgAdmin = false;
  // The org setting project_client_field (Sitenotes only) turns the Client field
  // on; a project that already has a client keeps showing it either way.
  showClient = false;

  ngOnInit(): void {
    const account = this.authService?.getCurrentUser();
    const roles = account?.roles || [];
    const projectRole = roles.length > 0
      ? roles.find((r: { project_id?: string }) => r.project_id === this.data?.id)?.role
      : undefined;
    const effectiveRole = projectRole ?? account?.role;
    this.isOrgAdmin = effectiveRole === 'org-admin';
    if (!this.isOrgAdmin) {
      this.form.removeControl('change_order_days');
    }

    const storedClient = this.data?.source_entity_extra_attributes?.client;
    this.store.select(appDataDetails).pipe(takeUntil(this.onDestroy)).subscribe((appData: { org_config?: Record<string, unknown> } | null) => {
      this.showClient = appData?.org_config?.project_client_field === true || !!storedClient;
    });

    if (this.data?.id || this.data?.name || this.data?.number) {
      this.form.patchValue({
        name: this.data?.name ?? null,
        number: this.data?.number ?? null,
        client: this.data?.source_entity_extra_attributes?.client ?? null,
        project_start_date: this.data?.start_date ? moment(this.data.start_date) : null,
        project_end_date: this.data?.end_date ? moment(this.data.end_date) : null,
        project_value: this.data?.project_value ?? null,
        change_order_days: this.data?.change_order_days ?? null,
        description: this.data?.description ?? null,
        status: this.data?.status ?? null,
        latitude: this.data?.latitude != null ? parseFloat(this.data.latitude) : null,
        longitude: this.data?.longitude != null ? parseFloat(this.data.longitude) : null,
      });
      this.mediaFiles = [];
      if (this.data?.cover_photo) {
        const cover = this.data.cover_photo;
        this.mediaFiles = [{ ...cover, fileType: cover.fileType || 'picture' }];
      }
      this.form.get('pictures')?.setValue(JSON.stringify(this.mediaFiles), { emitEvent: false });
    }

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];
        const newPicture = items.find((item: any) => item?.fileType === 'picture' || item?.fileType === 'document');
        if (newPicture) {
          this.mediaFiles = [newPicture];
          this.form.get('pictures')?.setValue(JSON.stringify(this.mediaFiles));
        }
      });

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  save(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }
    const raw = this.form.getRawValue();
    const startMoment = moment(raw.project_start_date);
    const endMoment = moment(raw.project_end_date);
    const pictures = this.getPictures();
    const payload: Record<string, unknown> = {
      id: this.data?.id,
      name: raw.name,
      number: raw.number,
      description: raw.description ?? '',
      start_date: startMoment.format('YYYY-MM-DD'),
      end_date: endMoment.format('YYYY-MM-DD'),
      duration: endMoment.diff(startMoment, 'd') + 1,
      project_value: raw.project_value,
      change_order_days: raw.change_order_days ?? 0,
      latitude: raw.latitude,
      longitude: raw.longitude,
      cover_photo_id: pictures?.[0]?.id ?? null,
      status: raw.status,
      // Merged server-side into the stored extra attributes; read by report
      // templates as extra_attributes.client. Blank clears it.
    };
    if (this.showClient) {
      payload.source_entity_extra_attributes = { client: raw.client?.trim() || null };
    }
    this.store.dispatch(UpdateProjectRequest({ payload }));
  }

  showLocationSection(): boolean {
    const lat = this.form?.controls?.latitude?.value;
    const lng = this.form?.controls?.longitude?.value;
    return lat != null && lat !== '' && lng != null && lng !== '';
  }

  openLocationPicker(): void {
    const location = {
      latitude: this.form.value.latitude,
      longitude: this.form.value.longitude,
    };
    const dialogRef = this.dialog.open(LocationPickerComponent, {
      disableClose: true,
      data: { ...location, readOnly: false },
    });
    dialogRef.afterClosed().subscribe(resp => {
      if (resp && 'latitude' in resp && 'longitude' in resp) {
        this.form.patchValue({
          latitude: resp.latitude,
          longitude: resp.longitude,
        });
      }
    });
  }

  editLocation(): void {
    this.openLocationPicker();
  }

  deleteLocation(): void {
    this.form.patchValue({ latitude: null, longitude: null });
  }

  deletePicture(picture: any): void {
    if (picture?.fileType === 'picture' || picture?.fileType === 'document') {
      const pictures = this.getPictures().filter((o: any) => o.id !== picture.id);
      this.mediaFiles = this.mediaFiles.filter((o: any) => o.id !== picture.id);
      this.form.get('pictures')?.setValue(JSON.stringify(pictures));
    }
  }

  getPictures(): any[] {
    const value = this.form.get('pictures')?.value || '[]';
    try {
      return JSON.parse(value) || [];
    } catch {
      return [];
    }
  }

  checkForErrors(currentField?: string): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField),
    };
  }
}
